import http from "./http";

/**
 * Return the investment policy for the active group.
 *
 * The frontend can use investment_enabled to decide whether
 * the investment menu, route, and management page should appear.
 */
export async function getInvestmentSettings() {
  const response = await http.get("/settings/rules");

  const rules = response?.data?.data ?? response?.data ?? {};

  return {
    enabled: Boolean(rules.investment_enabled),
    percentage: Number(rules.investment_percentage ?? 0),

    excludeFromLoanEligibility: Boolean(
      rules.exclude_invested_funds_from_loan_eligibility
    ),

    excludeFromWithdrawalEligibility: Boolean(
      rules.exclude_invested_funds_from_withdrawal_eligibility
    ),

    rules,
  };
}

/**
 * Check whether investments are enabled for the active group.
 */
export async function isInvestmentEnabled() {
  const settings = await getInvestmentSettings();

  return settings.enabled;
}

/**
 * List investments for the active group.
 *
 * Supported filters:
 * - status
 * - financial_year_rule_id
 * - from
 * - to
 * - q
 * - per_page
 * - page
 */
export function listInvestments(params = {}) {
  return http.get("/investments", {
    params,
  });
}

/**
 * Load one investment and its participant allocations.
 */
export function getInvestment(id) {
  if (!id) {
    return Promise.reject(
      new Error("Investment ID is required.")
    );
  }

  return http.get(`/investments/${id}`);
}

/**
 * Record a new investment.
 *
 * Expected payload:
 * {
 *   name: string,
 *   description?: string|null,
 *   invested_date: string,
 *   financial_year_rule_id?: number|null,
 *   total_amount?: number|null,
 *   participants: [
 *     {
 *       user_id: number,
 *       beneficiary_id?: number|null
 *     }
 *   ]
 * }
 *
 * eligible_funds must never be sent by the frontend.
 * The backend calculates participant funds.
 */
export function createInvestment(payload) {
  return http.post("/investments", payload);
}

/**
 * Sell or liquidate an investment.
 *
 * Expected payload:
 * {
 *   sale_amount: number,
 *   sale_date: string,
 *   status: "sold" | "liquidated",
 *   note?: string|null
 * }
 */
export function settleInvestment(id, payload) {
  if (!id) {
    return Promise.reject(
      new Error("Investment ID is required.")
    );
  }

  return http.post(
    `/investments/${id}/settle`,
    payload
  );
}

/**
 * Normalize an investment returned by the API.
 */
export function normalizeInvestment(investment = {}) {
  return {
    ...investment,

    id: Number(investment.id ?? 0),

    group_id: Number(
      investment.group_id ?? 0
    ),

    financial_year_rule_id:
      investment.financial_year_rule_id !== null
      && investment.financial_year_rule_id !== undefined
        ? Number(
            investment.financial_year_rule_id
          )
        : null,

    total_amount: Number(
      investment.total_amount ?? 0
    ),

    eligible_funds_snapshot: Number(
      investment.eligible_funds_snapshot ?? 0
    ),

    investment_percentage_snapshot: Number(
      investment.investment_percentage_snapshot ?? 0
    ),

    sale_amount:
      investment.sale_amount !== null
      && investment.sale_amount !== undefined
        ? Number(investment.sale_amount)
        : null,

    profit_loss:
      investment.profit_loss !== null
      && investment.profit_loss !== undefined
        ? Number(investment.profit_loss)
        : null,

    allocations: Array.isArray(
      investment.allocations
    )
      ? investment.allocations.map(
          normalizeInvestmentAllocation
        )
      : [],
  };
}

/**
 * Normalize one participant allocation.
 */
export function normalizeInvestmentAllocation(
  allocation = {}
) {
  return {
    ...allocation,

    id: Number(allocation.id ?? 0),

    investment_id: Number(
      allocation.investment_id ?? 0
    ),

    group_id: Number(
      allocation.group_id ?? 0
    ),

    user_id: Number(
      allocation.user_id ?? 0
    ),

    beneficiary_id:
      allocation.beneficiary_id !== null
      && allocation.beneficiary_id !== undefined
        ? Number(allocation.beneficiary_id)
        : null,

    eligible_funds_snapshot: Number(
      allocation.eligible_funds_snapshot ?? 0
    ),

    allocated_amount: Number(
      allocation.allocated_amount ?? 0
    ),

    principal_returned: Number(
      allocation.principal_returned ?? 0
    ),

    profit_loss_amount: Number(
      allocation.profit_loss_amount ?? 0
    ),
  };
}

/**
 * Extract paginated investment data safely.
 */
export function normalizeInvestmentListResponse(
  response
) {
  const payload = response?.data ?? {};
  const paginator = payload?.data ?? {};

  const rows = Array.isArray(paginator.data)
    ? paginator.data.map(normalizeInvestment)
    : [];

  return {
    rows,

    pagination: {
      currentPage: Number(
        paginator.current_page ?? 1
      ),

      lastPage: Number(
        paginator.last_page ?? 1
      ),

      perPage: Number(
        paginator.per_page ?? 15
      ),

      total: Number(
        paginator.total ?? rows.length
      ),

      from: paginator.from ?? null,
      to: paginator.to ?? null,
    },

    summary: {
      totalCount: Number(
        payload?.summary?.total_count ?? 0
      ),

      activePrincipal: Number(
        payload?.summary?.active_principal ?? 0
      ),

      totalSaleAmount: Number(
        payload?.summary?.total_sale_amount ?? 0
      ),

      totalProfitLoss: Number(
        payload?.summary?.total_profit_loss ?? 0
      ),
    },
  };
}

export default {
  getInvestmentSettings,
  isInvestmentEnabled,
  listInvestments,
  getInvestment,
  createInvestment,
  settleInvestment,
  normalizeInvestment,
  normalizeInvestmentAllocation,
  normalizeInvestmentListResponse,
};